/**
 * Sidequest countdown bar — <sidequest-countdown>
 * ───────────────────────────────────────────────
 * Dependency-free. No external requests, no libraries, no polyfills.
 *
 * Two responsibilities, deliberately kept apart:
 *
 *   1. The element ticks its own countdown, recomputing from Date.now() on every
 *      update so a throttled or backgrounded tab can never accumulate drift.
 *
 *   2. A single module-level controller (`Stack`) publishes the height of the
 *      *visible* sticky stack to `--sq-sticky-stack-height` on <html>. That is
 *      the greatest bottom edge, in viewport coordinates, of this bar and of
 *      Dawn's `.section-header` — measured, never guessed. Sticky page furniture
 *      (the bundle builder's group nav) sits on that number, so it tracks the
 *      header as Dawn slides it in and out instead of reserving the stored
 *      `--header-height` while the header is parked off-canvas.
 *
 * The controller is shared: two bars on one page still mean one scroll listener,
 * one rAF pipeline and one custom property.
 */

(function () {
  'use strict';

  /* The asset could be referenced by more than one section instance. Defining
     the element twice throws; re-attaching the listeners would double the work. */
  if (window.SidequestCountdownStack) return;

  var TAG = 'sidequest-countdown';
  var PROP = '--sq-sticky-stack-height';
  var HEADER_SELECTOR = '.section-header';

  /* Sub-pixel churn is not worth a style write; 0.25px is well below anything
     the sticky offsets can express. */
  var EPSILON = 0.25;

  /* Dawn animates the header with `transition: top 0.15s ease-out`. Class
     mutations tell us a reveal or hide has begun but not where it is mid-flight,
     so we measure every frame for the length of the transition plus slack. A
     transitionend on the header cuts the pump short. */
  var TRANSITION_TAIL = 420;

  /* ───────────────────────────── sticky stack ───────────────────────────── */

  var Stack = {
    bars: new Set(),
    header: null,
    frame: 0,
    pumpFrame: 0,
    pumpUntil: 0,
    listening: false,
    barResize: null,
    headerResize: null,
    headerMutations: null,
    last: -1,
  };

  /**
   * The bottom edge of an element in viewport coordinates, or 0 when it should
   * not reserve any space: detached, display:none, zero-sized, invisible, or
   * scrolled entirely above the viewport (which is exactly the state Dawn's
   * hidden header is in — `top: calc(-1 * var(--header-height))`).
   */
  function visibleBottom(element) {
    if (!element || !element.isConnected) return 0;

    var rect = element.getBoundingClientRect();
    if (!rect || rect.height <= 0 || rect.width <= 0) return 0;
    if (rect.bottom <= 0) return 0;

    var styles = window.getComputedStyle(element);
    if (!styles) return rect.bottom;
    if (styles.display === 'none' || styles.visibility === 'hidden') return 0;
    if (styles.opacity === '0') return 0;

    return rect.bottom;
  }

  /**
   * The header node is re-resolved lazily. A Theme Editor reload of the header
   * section swaps the element out from under us, and a stale reference measures
   * as zero forever after.
   */
  function resolveHeader() {
    if (Stack.header && Stack.header.isConnected) return Stack.header;

    var next = document.querySelector(HEADER_SELECTOR);
    if (next !== Stack.header) {
      detachHeaderObservers();
      Stack.header = next;
      attachHeaderObservers();
    }
    return Stack.header;
  }

  function measure() {
    if (!Stack.bars.size) return;

    var bottom = 0;

    Stack.bars.forEach(function (bar) {
      var edge = visibleBottom(bar);
      if (edge > bottom) bottom = edge;
    });

    var headerEdge = visibleBottom(resolveHeader());
    if (headerEdge > bottom) bottom = headerEdge;

    if (!(bottom > 0)) bottom = 0; // also catches NaN
    var value = Math.round(bottom * 100) / 100;

    if (Stack.last >= 0 && Math.abs(value - Stack.last) < EPSILON) return;
    Stack.last = value;
    document.documentElement.style.setProperty(PROP, value + 'px');
  }

  function onFrame() {
    Stack.frame = 0;
    measure();
  }

  /** Every scroll, resize and observer callback funnels through one rAF. */
  function schedule() {
    if (Stack.frame) return;
    Stack.frame = window.requestAnimationFrame(onFrame);
  }

  function onPumpFrame() {
    Stack.pumpFrame = 0;
    measure();
    if (Date.now() < Stack.pumpUntil) {
      Stack.pumpFrame = window.requestAnimationFrame(onPumpFrame);
    }
  }

  /** Measure every frame while the header is sliding. */
  function startPump() {
    Stack.pumpUntil = Date.now() + TRANSITION_TAIL;
    if (!Stack.pumpFrame) {
      Stack.pumpFrame = window.requestAnimationFrame(onPumpFrame);
    }
  }

  function onHeaderTransition(event) {
    if (event.target !== Stack.header) return;
    if (event.propertyName && event.propertyName !== 'top') return;
    Stack.pumpUntil = 0; // the slide has landed; one final measurement will do
    schedule();
  }

  function onSectionChange() {
    resolveHeader();
    schedule();
  }

  function attachHeaderObservers() {
    var header = Stack.header;
    if (!header) return;

    /* Dawn toggles `shopify-section-header-hidden`, `shopify-section-header-sticky`
       and `animate` on this element. Each of those moves its bottom edge. */
    if ('MutationObserver' in window) {
      Stack.headerMutations = new MutationObserver(startPump);
      Stack.headerMutations.observe(header, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    if ('ResizeObserver' in window) {
      Stack.headerResize = new ResizeObserver(schedule);
      Stack.headerResize.observe(header);
    }

    header.addEventListener('transitionend', onHeaderTransition);
    header.addEventListener('transitioncancel', onHeaderTransition);
  }

  function detachHeaderObservers() {
    if (Stack.headerMutations) {
      Stack.headerMutations.disconnect();
      Stack.headerMutations = null;
    }
    if (Stack.headerResize) {
      Stack.headerResize.disconnect();
      Stack.headerResize = null;
    }
    if (Stack.header) {
      Stack.header.removeEventListener('transitionend', onHeaderTransition);
      Stack.header.removeEventListener('transitioncancel', onHeaderTransition);
    }
  }

  function attachGlobalListeners() {
    if (Stack.listening) return;
    Stack.listening = true;

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });
    window.addEventListener('load', schedule);
    document.addEventListener('visibilitychange', schedule);
    document.addEventListener('shopify:section:load', onSectionChange);
    document.addEventListener('shopify:section:unload', onSectionChange);
  }

  function detachGlobalListeners() {
    if (!Stack.listening) return;
    Stack.listening = false;

    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    window.removeEventListener('load', schedule);
    document.removeEventListener('visibilitychange', schedule);
    document.removeEventListener('shopify:section:load', onSectionChange);
    document.removeEventListener('shopify:section:unload', onSectionChange);
  }

  function registerBar(bar) {
    Stack.bars.add(bar);
    attachGlobalListeners();

    if ('ResizeObserver' in window) {
      if (!Stack.barResize) Stack.barResize = new ResizeObserver(schedule);
      Stack.barResize.observe(bar);
    }
    /* No ResizeObserver (older Safari): the passive resize listener above is the
       fallback, plus the measurement every scroll frame. */

    resolveHeader();
    schedule();
  }

  function unregisterBar(bar) {
    Stack.bars.delete(bar);
    if (Stack.barResize) Stack.barResize.unobserve(bar);

    if (Stack.bars.size) {
      schedule();
      return;
    }

    /* Last bar gone — the section was removed or the editor is reloading it.
       Tear everything down and drop the property so the stylesheets fall back to
       their `var(--header-height, 0px)` default rather than freezing on a stale
       number. A reload re-registers a frame later and re-publishes. */
    detachHeaderObservers();
    detachGlobalListeners();
    if (Stack.frame) {
      window.cancelAnimationFrame(Stack.frame);
      Stack.frame = 0;
    }
    if (Stack.pumpFrame) {
      window.cancelAnimationFrame(Stack.pumpFrame);
      Stack.pumpFrame = 0;
    }
    if (Stack.barResize) {
      Stack.barResize.disconnect();
      Stack.barResize = null;
    }
    Stack.header = null;
    Stack.pumpUntil = 0;
    Stack.last = -1;
    document.documentElement.style.removeProperty(PROP);
  }

  /* ─────────────────────────────── the timer ─────────────────────────────── */

  function pad(value) {
    return value < 10 ? '0' + value : String(value);
  }

  /**
   * An absolute instant, parsed as ISO 8601 with its offset. Never derived from
   * the visitor's local midnight: `Date.parse` on a string carrying `Z` or a
   * numeric offset yields the same epoch value in every timezone.
   */
  function parseDeadline(value) {
    if (!value) return null;
    var parsed = Date.parse(String(value).trim());
    return isNaN(parsed) ? null : parsed;
  }

  function setHidden(node, hidden) {
    if (!node) return;
    if (node.hidden === hidden) return;
    node.hidden = hidden;
  }

  var SidequestCountdown = class extends HTMLElement {
    constructor() {
      super();
      this.timeout = 0;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      this.deadline = parseDeadline(this.getAttribute('data-deadline'));
      this.expiryBehavior = this.getAttribute('data-expiry-behavior') === 'hide' ? 'hide' : 'message';
      this.hideDaysWhenZero = this.getAttribute('data-hide-days-when-zero') !== 'false';
      this.isEnabled = this.getAttribute('data-enabled') !== 'false';

      this.nodes = {
        timer: this.querySelector('[data-sqcd-timer]'),
        expired: this.querySelector('[data-sqcd-expired]'),
        daysUnit: this.querySelector('[data-sqcd-unit="days"]'),
        days: this.querySelector('[data-sqcd-num="days"]'),
        hours: this.querySelector('[data-sqcd-num="hours"]'),
        minutes: this.querySelector('[data-sqcd-num="minutes"]'),
        seconds: this.querySelector('[data-sqcd-num="seconds"]'),
      };

      /* Registered whatever the bar's own state: a disabled, expired-hidden or
         timer-less bar still has to keep the sticky stack honest. */
      registerBar(this);

      if (!this.isEnabled) return;

      /* A missing or unparseable deadline is not an error state to shout about:
         the timer simply does not run, and the rest of the bar stands. The
         expired message is cleared too — a value the server could not read is
         not the same thing as an offer that has closed. */
      if (this.deadline === null) {
        setHidden(this.nodes.timer, true);
        setHidden(this.nodes.expired, true);
        if (this.getAttribute('data-state') !== 'live') {
          this.setAttribute('data-state', 'live');
          schedule();
        }
        return;
      }

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.tick();
    }

    disconnectedCallback() {
      this.clearTimer();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      unregisterBar(this);
    }

    clearTimer() {
      if (this.timeout) {
        window.clearTimeout(this.timeout);
        this.timeout = 0;
      }
    }

    /** A tab returning from the background may be many minutes stale. */
    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this.clearTimer();
      this.tick();
    }

    tick() {
      this.timeout = 0;

      /* Recomputed from the clock, not from a decremented counter, so throttled
         intervals and sleeping tabs cannot make the countdown wrong. */
      var remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        this.expire();
        return;
      }

      this.paint(remaining);

      /* Land on the next whole-second boundary of the *remaining* time so the
         digits change when they should, whatever the deadline's fraction. */
      var delay = remaining % 1000;
      if (delay < 30) delay += 1000;
      this.timeout = window.setTimeout(this.tick, delay);
    }

    paint(remaining) {
      var total = Math.floor(remaining / 1000);
      var days = Math.floor(total / 86400);
      var hours = Math.floor((total % 86400) / 3600);
      var minutes = Math.floor((total % 3600) / 60);
      var seconds = total % 60;

      /* Two digits minimum; three and four digit day counts are simply longer,
         so deadlines beyond 99 days keep counting rather than wrapping. */
      this.write(this.nodes.days, days < 100 ? pad(days) : String(days));
      this.write(this.nodes.hours, pad(hours));
      this.write(this.nodes.minutes, pad(minutes));
      this.write(this.nodes.seconds, pad(seconds));

      setHidden(this.nodes.daysUnit, this.hideDaysWhenZero && days === 0);
      setHidden(this.nodes.timer, false);
      setHidden(this.nodes.expired, true);

      if (this.getAttribute('data-state') !== 'live') {
        this.setAttribute('data-state', 'live');
        schedule();
      }
    }

    write(node, text) {
      if (!node) return;
      if (node.textContent === text) return; // no needless layout work per tick
      node.textContent = text;
    }

    expire() {
      this.clearTimer();

      if (this.expiryBehavior === 'hide') {
        this.setAttribute('data-state', 'hidden');
      } else {
        this.setAttribute('data-state', 'expired');
        setHidden(this.nodes.timer, true);
        setHidden(this.nodes.expired, false);
      }

      /* The bar just changed height (or vanished); republish immediately. */
      schedule();
    }
  };

  if (!customElements.get(TAG)) {
    customElements.define(TAG, SidequestCountdown);
  }

  /* Doubles as the re-execution guard and as a debugging handle:
     `SidequestCountdownStack.last` is the published value. */
  window.SidequestCountdownStack = Stack;
})();